import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import { useMemo } from 'react';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';

export const useGetAndSetRunTimeBlockState = ({
  originalBlockState,
  blockStateId,
  runtimeBlockKey,
}: {
  originalBlockState: BlockStateUnionType | undefined;
  // here blockStateId might look like runtime_block_block1_1
  blockStateId: string | undefined;
  runtimeBlockKey: string | undefined;
}): BlockStateUnionType | undefined => {
  const runTimeBlockState = useGlobalStateStore().hooks.block.useDetails(blockStateId);

  // Build the runtime block state once per (source-state, id) change instead of
  // every render. Both `runTimeBlockState` and `originalBlockState` are stable
  // store references (unchanged block → same ref), so this memo holds across the
  // frequent re-renders a runtime block gets from context/store fan-out. Without
  // it, the fresh object minted every render is a new reference that defeats the
  // `memo` on withContext/withBlockComputations, busts the `blockState`-keyed
  // useMemo in useBlockDynamicAndConditionalPaths (forcing a dependency rebuild),
  // and — when the block has no dynamic/conditional paths — becomes a churning
  // `computedBlockState` that re-renders the leaf <Block>. The value is identical
  // either way; only the reference is stabilized.
  return useMemo(() => {
    // Not inside a runtime context provider → the original block state passes
    // through untouched. Checked first, inside the memo, so a non-runtime block
    // (the common case off the Repeatable path) allocates nothing.
    if (runtimeBlockKey === undefined) {
      return originalBlockState;
    }

    // No materialized entry yet — synthesize from the original. The first
    // setBlockState/updateBlockState write on this runtime ID goes through
    // materializeInstanceState (createGlobalStateStore) and produces a fully
    // materialized entry; from that point on we read it directly. Builder-side
    // template edits keep every materialized instance in sync via the fanout in
    // getUpdateBlockStateByActiveDeviceAction, so no live overlay is needed here.
    if (runTimeBlockState === undefined) {
      return {
        ...originalBlockState,
        id: blockStateId,
        originalBlockId: originalBlockState?.id,
      } as BlockStateUnionType;
    }

    return {
      ...runTimeBlockState,
      id: blockStateId,
      originalBlockId: originalBlockState?.id,
    } as BlockStateUnionType;
  }, [runtimeBlockKey, runTimeBlockState, originalBlockState, blockStateId]);
};
